// src/crm/templates/TemplatePicker.tsx
import type { Creator, Deal, MessageChannel } from '../../lib/types'
import { useAppStore } from '../../shell/appStore'
import { useMessageTemplates } from './useMessageTemplates'
import { resolveVariables } from './resolve'

type Props = {
  channel: MessageChannel
  contact: Creator
  deal: Deal | null
  appName: string
  onApply: (r: { subject: string | null; body: string; missing: string[] }) => void
}

export function TemplatePicker({ channel, contact, deal, appName, onApply }: Props) {
  const { activeAppId } = useAppStore()
  const { data: templates = [] } = useMessageTemplates(activeAppId, { channel })

  // Templates du canal, ciblant le type du contact ou tous types
  const relevant = templates.filter(
    (t) => t.contactType === null || t.contactType === contact.contactType,
  )

  if (relevant.length === 0) return null

  function handleSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const tpl = relevant.find((t) => t.id === e.target.value)
    if (!tpl) return
    const ctx = { contact, deal, appName }
    const body = resolveVariables(tpl.body, ctx)
    const subject = tpl.subject != null ? resolveVariables(tpl.subject, ctx) : null
    onApply({
      subject: subject?.text ?? null,
      body: body.text,
      missing: [...new Set([...(subject?.missing ?? []), ...body.missing])],
    })
    e.target.value = ''
  }

  return (
    <div style={{ marginBottom: '0.25rem' }}>
      <select
        aria-label="Utiliser un template"
        defaultValue=""
        onChange={handleSelect}
        style={{
          background: 'var(--elevated)',
          border: '1px solid var(--line-strong)',
          borderRadius: '7px',
          padding: '0.375rem 0.625rem',
          color: 'var(--muted)',
          fontSize: '0.8125rem',
        }}
      >
        <option value="" disabled>Utiliser un template…</option>
        {relevant.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.language.toUpperCase()})
          </option>
        ))}
      </select>
    </div>
  )
}
